import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type ReactNode,
} from 'react';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { useClickToReveal } from '~/modules/root/hook/use-click-to-reveal';
import { BusinessFocusEnum } from 'generated/graphql';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { buildProfessionalTrustSignals } from '../utils/professional-trust-signals';

/** `useLayoutEffect` warns on the server; the measurement is browser-only. */
const useIsomorphicLayoutEffect =
    typeof document === 'undefined' ? useEffect : useLayoutEffect;

/**
 * A list of values kept to a single line, with the remainder behind a count.
 *
 * A wrapped value is the thing that breaks this grid: the row below it shifts
 * and the two columns stop lining up. Rather than cap the list at a guessed
 * number of items — three short services fit where three long ones do not —
 * this renders them all, finds the first one the browser pushed onto a second
 * line, and keeps what came before it. Opening the count shows the rest, and
 * at that point wrapping is what the reader asked for.
 *
 * One item slot is given up when there is a remainder, so the count itself has
 * somewhere to sit: "+3" is narrower than any value that would have filled it.
 */
const SpecInlineList = ({ items }: { items: ReactNode[] }) => {
    const ref = useRef<HTMLSpanElement>(null);
    // null means "showing everything, waiting to be measured".
    const [limit, setLimit] = useState<number | null>(null);
    const [expanded, setExpanded] = useState(false);

    const remeasure = useCallback(() => setLimit(null), []);

    useIsomorphicLayoutEffect(() => {
        if (expanded) return;
        const host = ref.current;
        if (!host) return;
        const parts = Array.from(
            host.querySelectorAll<HTMLElement>('[data-spec-item]'),
        );
        const [first] = parts;
        if (!first || parts.length < 2) return;
        const firstLine = first.offsetTop;

        // Cut, and then keep cutting until the count sits on the line too.
        // The slot given up for it is usually enough, but not always: a
        // value that only just fit leaves less room behind it than "+6"
        // needs, and a count on a second line is the very wrap this exists
        // to prevent.
        if (limit !== null) {
            const count = host.querySelector<HTMLElement>('[data-spec-more]');
            if (count && count.offsetTop > firstLine && limit > 1) {
                setLimit(limit - 1);
            }
            return;
        }

        const wrapped = parts.findIndex((part) => part.offsetTop > firstLine);
        if (wrapped === -1) {
            setLimit(parts.length);
            return;
        }
        setLimit(Math.max(1, wrapped - 1));
    }, [expanded, limit, items.length]);

    useEffect(() => {
        if (expanded) return;
        // The column is fluid, so what fits changes with the window.
        window.addEventListener('resize', remeasure);
        // And again once the real face is in: measured against the fallback,
        // a line that will wrap in Neue Haas Grotesk often does not, and the
        // first measurement is the only one there would otherwise be.
        let live = true;
        void document.fonts?.ready.then(() => {
            if (live) remeasure();
        });
        return () => {
            live = false;
            window.removeEventListener('resize', remeasure);
        };
    }, [expanded, remeasure]);

    const measuring = limit === null;
    const shown = expanded || measuring ? items : items.slice(0, limit);
    const hidden = items.length - shown.length;

    return (
        <span ref={ref}>
            {shown.map((item, index) => (
                <span key={index} data-spec-item>
                    {index > 0 ? ', ' : null}
                    {item}
                </span>
            ))}
            {hidden > 0 ? (
                <button
                    type="button"
                    data-spec-more
                    onClick={() => setExpanded(true)}
                    className="ml-[0.375rem] cursor-pointer text-[#141414]/50 [text-transform:inherit] transition-colors hover:text-[#141414]"
                >
                    +{hidden}
                </button>
            ) : null}
        </span>
    );
};

// Partial on purpose: `Unspecified` has no line to show, and a row with no
// value drops out rather than printing an empty label.
export const BUSINESS_FOCUS_TEXT: Partial<Record<BusinessFocusEnum, string>> = {
    [BusinessFocusEnum.Mixed]: 'Residential & Commercial',
    [BusinessFocusEnum.Commercial]: 'Commercial',
    [BusinessFocusEnum.Residential]: 'Residential',
};

/**
 * A contact detail held back behind "View address" / "View phone number", as
 * on the live profile. The click is the lead — it is logged against the
 * professional and remembered for the session, so the reader reveals once.
 */
const SpecReveal = ({
    label,
    value,
    revealType,
    professionalId,
    href,
}: {
    label: string;
    value?: string;
    revealType: 'Location' | 'Phone';
    professionalId: number;
    href?: string;
}) => {
    const { revealed, onRevealClick } = useClickToReveal({
        revealType,
        revealKey: `professional-${professionalId}`,
        professionalId,
    });
    if (!value) return null;

    return (
        <>
            <dt className="text-[#141414]/50">{label}</dt>
            <dd className="m-0 text-[#141414]">
                {revealed ? (
                    href ? (
                        <a
                            href={href}
                            className="[text-transform:inherit] transition-colors hover:text-[#767676]"
                        >
                            {value}
                        </a>
                    ) : (
                        value
                    )
                ) : (
                    <button
                        type="button"
                        onClick={onRevealClick}
                        className="cursor-pointer [text-transform:inherit] transition-colors hover:text-[#767676]"
                    >
                        {revealType === 'Phone'
                            ? 'View phone number'
                            : 'View address'}
                    </button>
                )}
            </dd>
        </>
    );
};

/**
 * The networks they are on, named and linked, rather than a row of glyphs.
 *
 * A word is a bigger target than a 24px icon, it says which network it is
 * without the reader having to recognise a mark, and it lets this sit as one
 * more row in the grid instead of a separate strip under it.
 */
const SOCIAL_NETWORKS = [
    { key: 'instagram', label: 'Instagram' },
    { key: 'linkedin', label: 'LinkedIn' },
    { key: 'youtube', label: 'YouTube' },
    { key: 'pinterest', label: 'Pinterest' },
    { key: 'facebook', label: 'Facebook' },
] as const;

const SpecSocialRow = ({
    links,
}: {
    links?: Partial<Record<string, string | null>> | null;
}) => {
    const present = SOCIAL_NETWORKS.map((network) => ({
        ...network,
        href: links?.[network.key],
    })).filter((network): network is typeof network & { href: string } =>
        Boolean(network.href),
    );
    if (!present.length) return null;

    return (
        <>
            <dt className="text-[#141414]/50">Social</dt>
            <dd className="m-0 text-[#141414]">
                <SpecInlineList
                    items={present.map((network) => (
                        <RoccoLink
                            key={network.key}
                            to={network.href}
                            newTab
                            className="[text-transform:inherit] transition-colors hover:text-[#767676]"
                        >
                            {network.label}
                        </RoccoLink>
                    ))}
                />
            </dd>
        </>
    );
};

/** Grey label / list-of-values pair, kept to one line. */
const SpecListRow = ({ label, values }: { label: string; values: string[] }) =>
    values.length ? (
        <>
            <dt className="text-[#141414]/50">{label}</dt>
            <dd className="m-0 text-[#141414]">
                <SpecInlineList items={values} />
            </dd>
        </>
    ) : null;

/** Grey label / ink value pair. Rendered only when there is a value. */
const SpecRow = ({ label, value }: { label: string; value?: string }) =>
    value ? (
        <>
            <dt className="text-[#141414]/50">{label}</dt>
            <dd className="m-0 text-[#141414]">{value}</dd>
        </>
    ) : null;

/**
 * The practice's facts as a label/value grid.
 *
 * Rows without data drop out, so a thin profile shows a short list rather than
 * a column of empty labels. `compact` is the identity card's setting: the same
 * rows at the card's own small scale, in one narrow column rather than two
 * wide ones.
 */
export const ProfessionalSpecList = ({
    compact = false,
    singleColumn = false,
}: {
    compact?: boolean;
    /**
     * One label/value pair per row, at the full size. For the summary block,
     * where the list sits in a column on the right of the page rather than
     * running the whole lane — two pairs to a row need the width of the page
     * to sit apart, and in a column they read as four ragged columns.
     */
    singleColumn?: boolean;
}) => {
    const initial = useProfessionalInitialRouteData();
    if (!initial) return null;

    const { typicalJob, associations } = buildProfessionalTrustSignals(initial);
    const primaryLocation = (initial.LibraryLocations ?? []).find(
        (location) => !location.LocationHidden,
    );
    const address =
        initial.PrimaryAddress ??
        [
            primaryLocation?.Address1,
            primaryLocation?.Suburb,
            primaryLocation?.City,
        ]
            .filter(Boolean)
            .join(', ');
    // The tracking number when there is one: the same line, routed so the call
    // is attributed to the profile.
    const phone = initial.PhoneTracking || initial.Phone || undefined;

    return (
        <dl
            className={cn(
                // Label columns are sized against the copy, not fixed: at 14px
                // the width that suited 20px copy strands each label a long
                // way from its value.
                'm-0 grid font-sans tracking-[0.02em]',
                compact
                    ? 'grid-cols-[7.5rem_1fr] gap-y-[0.25rem] text-[0.9375rem] leading-[1.45]'
                    : singleColumn
                      ? cn(
                            'text-[1.125rem] leading-[1.45] md:gap-y-[0.375rem]',
                            'md:grid-cols-[auto_minmax(0,1fr)] md:gap-x-[1.75rem]',
                            // Caps and a step heavier than the body: in a
                            // column on the right these are a reference table
                            // rather than a run of prose, and at 400 they read
                            // as an aside to the statement beside them.
                            'font-medium uppercase',
                        )
                      : cn(
                            'text-[1.125rem] leading-[1.45] md:gap-y-[0.1875rem]',
                            // Two label/value pairs to a row. The rows flow left
                            // to right, so a profile with an odd number of facts
                            // leaves one gap at the end rather than a ragged
                            // column. Capped, or the pairs drift to opposite
                            // ends of a very wide page.
                            //
                            // The label columns take their content's width
                            // rather than a fixed one: fixed, the column has to
                            // fit "Typical Project Budget" and every shorter
                            // label is then left stranded a long way from its
                            // value. The value columns take the slack instead,
                            // which is also what keeps the two pairs apart.
                            'md:max-w-[78rem] md:grid-cols-[auto_minmax(0,1fr)_auto_minmax(0,1fr)] md:gap-x-[1.75rem]',
                        ),
            )}
        >
            <SpecReveal
                label="Location"
                value={address}
                revealType="Location"
                professionalId={initial.ID}
            />
            <SpecReveal
                label="Phone"
                value={phone}
                revealType="Phone"
                professionalId={initial.ID}
                href={phone ? `tel:${phone.replace(/[^+\d]/g, '')}` : undefined}
            />
            {/* Serviceable area has no field on Professional yet — the row
                appears as soon as one exists. */}
            <SpecRow label="Serviceable area" value={undefined} />
            <SpecRow
                label="Typical Project Budget"
                value={typicalJob ?? undefined}
            />
            <SpecRow
                label="Business Focus"
                value={
                    initial.BusinessFocus
                        ? BUSINESS_FOCUS_TEXT[initial.BusinessFocus]
                        : undefined
                }
            />
            <SpecListRow
                label="Services"
                // Trimmed: the CMS values carry stray whitespace ("Bathware "),
                // which reads as "Bathware , Residential" once the separator is
                // added. No slice — the row shows what fits on its line and
                // counts the rest, which a fixed three cannot do when the three
                // are long ones.
                values={
                    initial.Services?.map((service) => service.trim()).filter(
                        Boolean,
                    ) ?? []
                }
            />
            <SpecRow label="Associations" value={associations?.label} />
            <SpecSocialRow links={initial.SocialLinks} />
        </dl>
    );
};
